import * as React from "react";
import { Grid, Input, Typography, makeStyles, Theme, Button, Select, MenuItem } from "@material-ui/core";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import { Add, Close } from "@material-ui/icons";
import { HandicapAdjustmentRule, AdjustmentType } from "./../../types/competition";

export interface HandicapControlProps {
  handicapAdjustments: HandicapAdjustmentRule[];
  onChanged: (event: any) => void;
  isVisible: boolean;
  allowAverageBound?: boolean;
}

const HandicapControl: React.SFC<HandicapControlProps> = props => {
  const classes = useStyles();
  const { handicapAdjustments, isVisible, allowAverageBound = false } = props;

  const handleOnTextChanged = (index: number, field: string) => (event: any) => {
    let adjustments = [...handicapAdjustments];
    adjustments[index] = { ...adjustments[index], [field]: event.target.value as number };
    props.onChanged(adjustments);
  };

  const setAverageBound = (
    index: number,
    flag: "greaterThanIsAverage" | "lessThanIsAverage",
    isAverage: boolean,
  ) => {
    const adjustments = [...handicapAdjustments];
    adjustments[index] = { ...adjustments[index], [flag]: isAverage };
    props.onChanged(adjustments);
  };

  const addNewRule = () => {
    const initData: HandicapAdjustmentRule = { greaterThan: 0, lessThan: 100, value: 0, type: AdjustmentType.OVERALL };
    props.onChanged(handicapAdjustments ? handicapAdjustments.concat(initData) : [initData]);
  };

  const removeRule = (index: number) => {
    handicapAdjustments.splice(index, 1);
    props.onChanged(handicapAdjustments);
  };

  return !isVisible ? null : (
    <div>
      {handicapAdjustments?.map((handicap, index: number) => {
        return (
          <Grid container key={index} className={[classes.container, classes.blinkBackground].join(" ")}>
            <Grid md={12} xs={12} item className={classes.closeButton}>
              <Close color="secondary" onClick={() => removeRule(index)} />
            </Grid>
            <Grid item xs={12} className={classes.boundRow}>
              <Typography variant={"subtitle1"}>If player points score is greater than </Typography>
              <BoundValue
                classes={classes}
                allowAverage={allowAverageBound}
                isAverage={!!handicap.greaterThanIsAverage}
                value={handicap.greaterThan}
                onNumberChange={handleOnTextChanged(index, "greaterThan")}
                onSetAverage={next => setAverageBound(index, "greaterThanIsAverage", next)}
                labelledBy="greater than"
              />
            </Grid>
            <Grid item xs={12} className={classes.boundRow}>
              <Typography variant={"subtitle1"}>and less than </Typography>
              <BoundValue
                classes={classes}
                allowAverage={allowAverageBound}
                isAverage={!!handicap.lessThanIsAverage}
                value={handicap.lessThan}
                onNumberChange={handleOnTextChanged(index, "lessThan")}
                onSetAverage={next => setAverageBound(index, "lessThanIsAverage", next)}
                labelledBy="less than"
              />
            </Grid>
            <Grid item md={2} xs={6}>
              <Typography variant={"subtitle1"}>then </Typography>
            </Grid>
            <Grid item md={10} xs={6} className={classes.inputContainer}>
              <Select id="adjustmentType" value={handicap.type} onChange={handleOnTextChanged(index, "type")}>
                <MenuItem value={AdjustmentType.OVERALL}>Overall</MenuItem>
                <MenuItem value={AdjustmentType.PER_POINT}>Per Point</MenuItem>
              </Select>
            </Grid>
            <Grid item md={9} xs={9}>
              <Typography variant={"subtitle1"}>adjust handicap by</Typography>
            </Grid>
            <Grid item md={1} xs={1}>
              <Typography variant="h5" gutterBottom>
                {!handicap.value ? null : handicap.value < 0 ? "-" : "+"}{" "}
              </Typography>
            </Grid>
            <Grid item md={2} xs={2} className={classes.inputContainer}>
              <Input
                type={"number"}
                inputProps={{ step: 0.1 }}
                value={handicap.value}
                onChange={handleOnTextChanged(index, "value")}
                className={classes.smallInput}
              />
            </Grid>
          </Grid>
        );
      })}
      <div>
        <Button fullWidth style={{ marginTop: 8 }} color={"primary"} variant={"outlined"} onClick={addNewRule}>
          <Add />
        </Button>
      </div>
    </div>
  );
};

type BoundValueProps = {
  classes: ReturnType<typeof useStyles>;
  allowAverage: boolean;
  isAverage: boolean;
  value: number;
  onNumberChange: (event: any) => void;
  onSetAverage: (isAverage: boolean) => void;
  labelledBy: string;
};

const BoundValue = ({
  classes,
  allowAverage,
  isAverage,
  value,
  onNumberChange,
  onSetAverage,
  labelledBy,
}: BoundValueProps) => {
  if (!allowAverage) {
    return (
      <Input type={"number"} value={value} onChange={onNumberChange} className={classes.smallInput} />
    );
  }

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={isAverage ? "avg" : "number"}
      onChange={(_event, next) => {
        if (next == null) return;
        onSetAverage(next === "avg");
      }}
      className={classes.seg}
      aria-label={`${labelledBy} number or average`}
    >
      <ToggleButton value="number" className={classes.segNumber} aria-label={`${labelledBy} a number`}>
        {isAverage ? (
          <span className={classes.segNumberIdle}>{value}</span>
        ) : (
          <Input
            type="number"
            value={value}
            onChange={onNumberChange}
            onClick={event => event.stopPropagation()}
            disableUnderline
            className={classes.segInput}
            inputProps={{ "aria-label": `${labelledBy} number` }}
          />
        )}
      </ToggleButton>
      <ToggleButton value="avg" className={classes.segAvg} aria-label={`${labelledBy} average`}>
        Avg
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    border: "1px dashed #aaaaaa",
    borderRadius: 5,
    padding: 10,
    marginTop: 10,
  },
  smallInput: {
    width: 60,
  },
  inputContainer: {
    textAlign: "right",
  },
  boundRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    minHeight: 44,
  },
  seg: {
    flexShrink: 0,
    backgroundColor: "rgba(15, 23, 42, 0.04)",
  },
  segNumber: {
    minWidth: 64,
    minHeight: 36,
    padding: "0 6px",
    textTransform: "none",
    borderColor: "rgba(15, 23, 42, 0.14)",
    color: theme.palette.text.primary,
    "&.Mui-selected": {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.18)",
      "&:hover": {
        backgroundColor: theme.palette.background.paper,
      },
    },
  },
  segAvg: {
    minWidth: 52,
    minHeight: 36,
    padding: "0 10px",
    textTransform: "none",
    fontWeight: 700,
    letterSpacing: "0.04em",
    borderColor: "rgba(15, 23, 42, 0.14)",
    color: theme.palette.text.secondary,
    "&.Mui-selected": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
  segInput: {
    width: 48,
    fontSize: "1rem",
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
  },
  segNumberIdle: {
    display: "inline-block",
    minWidth: 48,
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    color: theme.palette.text.disabled,
  },
  closeButton: {
    textAlign: "right",
  },
  blinkBackground: {
    animation: `$blink 2s infinite`,

    animationIterationCount: 1,
  },
  "@keyframes blink": {
    from: { background: "rgba(250,237,57,0.9)" },
    to: { background: "rgba(250,237,57,0.0)" },
  },
}));

export default HandicapControl;
